<template>
  <v-dialog :value="showLibrary" fullscreen transition="fade-transition" @input="onDialogInput">
    <div class="library-shell" @click.self="onClose">
      <div class="library-panel" @click.stop>
        <v-card class="library-card rounded-lg" elevation="8">
          <v-card-title class="library-card__title d-flex align-center justify-space-between">
            <span class="library-card__title-text">Prompt library</span>
            <v-btn icon aria-label="Close" @click="onClose">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="library-card__body pa-0">
            <div class="library-layout">
              <aside class="library-left">
                <v-text-field
                  v-model="searchQuery"
                  class="library-left__search"
                  dense
                  outlined
                  hide-details
                  clearable
                  placeholder="Search prompt templates"
                  append-icon="mdi-magnify"
                  @keyup.enter="commitSearch"
                  @blur="commitSearch"
                />

                <div class="library-left__scroller">
                  <div v-if="templatesLoading" class="library-left__loading pa-8 d-flex justify-center">
                    <v-progress-circular indeterminate color="primary" size="32" width="3" />
                  </div>
                  <div v-else class="library-left__grid" role="list" aria-label="Templates">
                    <button
                      v-for="t in promptTemplates"
                      :key="t._id"
                      type="button"
                      class="template-tile"
                      :class="{ 'template-tile--active': selectedTemplateId === t._id }"
                      role="listitem"
                      @click="selectTemplate(t)"
                    >
                      <div class="template-tile__header">
                        <v-icon size="16">mdi-auto-fix</v-icon>
                        <div>Prompt</div>
                      </div>
                      <div class="template-tile__body">
                        <div class="template-tile__prompt" role="presentation">{{ (t.content || '').trim() }}</div>
                      </div>

                      <div class="template-tile__footer">
                        <div
                          class="template-tile__media"
                          :class="{ 'template-tile__media--empty': !templateThumbUrls(t).length }"
                        >
                          <template v-for="(u, i) in templateThumbUrls(t).slice(0, templateTilePreviewLimit)">
                            <img
                              v-if="!checkIsVideoUrl(u)"
                              :key="`img-${t.id}-${i}`"
                              class="template-tile__thumb"
                              :src="imageHelpers.url(u)"
                              alt=""
                            />
                            <div v-else :key="`vid-${t.id}-${i}`" class="template-tile__thumb--video">
                              <v-icon class="template-tile__thumb--video-icon" size="16" color="white">mdi-play</v-icon>
                              <video
                                :key="`vid-${t.id}-${i}`"
                                class="template-tile__thumb"
                                :src="imageHelpers.url(u)"
                                muted
                                playsinline
                              />
                            </div>
                          </template>
                          <div
                            v-if="templateAttachmentOverflowCount(t) > 0"
                            class="template-tile__overflow"
                            :title="
                              `${templateAttachmentOverflowCount(t)} more ${
                                templateAttachmentOverflowCount(t) > 1 ? 'images' : 'image'
                              } not shown`
                            "
                            :aria-label="
                              `${templateAttachmentOverflowCount(t)} more ${
                                templateAttachmentOverflowCount(t) > 1 ? 'images' : 'image'
                              } not shown in preview`
                            "
                          >
                            +{{ templateAttachmentOverflowCount(t) }}
                          </div>
                          <div v-if="!templateThumbUrls(t).length" class="template-tile__placeholder">
                            <div class="template-tile__placeholder-icon">
                              <v-icon size="28" color="grey lighten-1">mdi-text-box-outline</v-icon>
                            </div>
                          </div>
                        </div>
                      </div>

                      <v-btn
                        @click.stop="toggleSelectPrompt(t)"
                        v-if="selectedTemplateId === t._id"
                        icon
                        small
                        class="template-tile__toggle"
                        :aria-label="a"
                        :title="a"
                      >
                        <v-icon size="14" color="primary">mdi-check</v-icon>
                      </v-btn>
                      <v-btn
                        @click.stop="selectTemplate(t)"
                        v-else
                        icon
                        small
                        class="template-tile__toggle"
                        :aria-label="a"
                        :title="a"
                      >
                      </v-btn>
                    </button>
                  </div>
                  <div v-if="!templatesLoading && !promptTemplates.length" class="library-left__empty pa-6 text-center">
                    No templates match your search.
                  </div>
                </div>

                <div
                  v-if="!templatesLoading && paginationTotalPages > 1"
                  class="library-left__pagination d-flex justify-center"
                >
                  <v-pagination
                    :value="pagination.page"
                    :length="paginationTotalPages"
                    :total-visible="7"
                    @input="onPaginationPage"
                  />
                </div>
              </aside>

              <section class="library-right">
                <div class="image-generation-dialog__current-model">
                  Images are currently generated using <span class="font-weight-bold">ChatGPT</span> model.
                </div>
                <input
                  ref="libraryFileInput"
                  type="file"
                  class="prompt-templates-library__file-input"
                  accept="image/png,image/jpeg,image/webp"
                  @change="onFilesSelected"
                />

                <div class="library-right__upload-toolbar">
                  <div
                    class="library-right__upload-zone"
                    role="button"
                    tabindex="0"
                    @click="$refs.libraryFileInput.click()"
                    @keydown.enter.prevent="$refs.libraryFileInput.click()"
                    @keydown.space.prevent="$refs.libraryFileInput.click()"
                  >
                    <v-icon size="28" class="mb-1">mdi-cloud-upload-outline</v-icon>
                    <div class="upload-zone__title">Upload image</div>
                    <div class="upload-zone__subtitle">
                      Up to {{ maxAttachments }} {{ maxAttachments > 1 ? 'images' : 'image' }}, max 5MB
                      {{ maxAttachments > 1 ? 'each' : '' }} (PNG, JPG, JPEG, WEBP)
                    </div>
                  </div>
                  <div class="text-center">or</div>
                  <v-btn
                    outlined
                    class="library-right__url-btn"
                    height="auto"
                    :disabled="attachments.length >= maxAttachments"
                    @click="openAddMediaFromUrlDialog"
                  >
                    <v-icon left size="18">mdi-link-variant</v-icon>
                    Add from URL
                  </v-btn>
                </div>

                <div v-if="attachments.length" class="library-right__attachments">
                  <div class="attachments-strip" role="list" aria-label="Attachments">
                    <div v-for="att in attachments" :key="att.id" class="attachment" role="listitem">
                      <button
                        type="button"
                        class="attachment__thumb"
                        :aria-label="`Preview ${att.isVideo ? 'video' : 'image'}`"
                        :title="`Preview ${att.isVideo ? 'video' : 'image'}`"
                        :disabled="att.status !== 'ready'"
                        @click="openPreview(att)"
                      >
                        <div v-if="att.isVideo" class="attachment__thumb--video">
                          <v-icon class="attachment__thumb--video-icon" size="22" color="white">mdi-play</v-icon>
                          <video class="attachment__img" :src="att.url" muted playsinline />
                        </div>
                        <img v-else class="attachment__img" :src="att.url" :alt="att.name" />
                        <div v-if="att.status === 'uploading'" class="attachment__overlay" aria-label="Uploading">
                          <v-progress-circular indeterminate size="18" width="2" color="white" />
                        </div>
                        <div
                          v-else-if="att.status === 'error'"
                          class="attachment__overlay attachment__overlay--error"
                          aria-label="Upload failed"
                        >
                          <v-icon size="18" color="white">mdi-alert-circle</v-icon>
                        </div>
                      </button>
                      <button
                        type="button"
                        class="attachment__remove"
                        :aria-label="`Remove ${att.isVideo ? 'video' : 'image'}`"
                        :title="`Remove ${att.isVideo ? 'video' : 'image'}`"
                        @click="removeAttachment(att.id)"
                      >
                        <v-icon size="14">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <p class="library-right__prompt-label">Your prompt</p>
                  <textarea
                    v-model="message"
                    class="library-right__textarea"
                    rows="8"
                    placeholder="Prompt"
                    spellcheck="false"
                    autocorrect="off"
                    autocomplete="off"
                    autocapitalize="off"
                  />
                </div>
              </section>
            </div>
          </v-card-text>

          <v-card-actions class="library-card__actions px-4 pb-4">
            <v-spacer />
            <v-btn text @click="onClose">Cancel</v-btn>
            <v-btn class="ml-2" color="primary" :disabled="!canApply" @click="onSubmit">Use this prompt</v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </div>

    <PreviewImage
      v-if="preview.src && !preview.isVideo"
      :src="preview.src"
      :show-preview="preview.show"
      @close="closePreview"
    />
    <PreviewVideo
      v-if="preview.src && preview.isVideo"
      :src="preview.src"
      :show-preview="preview.show"
      @close="closePreview"
    />

    <AddMediaFromUrlDialog
      v-model="showAddMediaUrlModal"
      media-types="image"
      :max-attachments="maxAttachments"
      :attachments-count="attachments.length"
      @save="onSaveMediaFromUrlDialog"
    />
  </v-dialog>
</template>

<script>
import attachmentUploadMixin from './utils/attachment-upload-mixin';
import PreviewImage from './PreviewImage.vue';
import PreviewVideo from './PreviewVideo.vue';
import AddMediaFromUrlDialog from './AddMediaFromUrlDialog.vue';
import { isVideoUrl, filenameFromMediaUrl, isAllowedImageUrl } from './utils';
import { reportApi } from '@/apis/report';
import imageHelpers from '@/helpers/image';

export default {
  name: 'PromptTemplatesLibrary',
  components: { PreviewImage, PreviewVideo, AddMediaFromUrlDialog },
  mixins: [attachmentUploadMixin],
  props: {
    showLibrary: { type: Boolean, default: false },
    product: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      imageHelpers,
      searchQuery: '',
      templatesLoading: false,
      selectedTemplateId: null,
      message: '',
      templateTilePreviewLimit: 3,
      preview: {
        show: false,
        src: '',
        isVideo: false,
      },
      promptTemplates: [],
      pagination: {
        page: 1,
        limit: 24,
        total: 0,
        totalPage: 1,
      },
      /** Last successful list request; used to skip redundant commitSearch (blur/enter). */
      lastListFetchKey: null,
      showAddMediaUrlModal: false,
    };
  },
  mounted() {
    this.getPromptTemplates();
  },
  computed: {
    catalogId() {
      return this.product?.catalog?._id || '';
    },
    paginationTotalPages() {
      const { total, limit } = this.pagination;
      if (!limit) return 1;
      return Math.max(1, Math.ceil(total / limit));
    },
    canApply() {
      const text = (this.message || '').trim();
      const hasReadyAttachments =
        Array.isArray(this.attachments) &&
        this.attachments.some(a => a && a.status === 'ready' && (a.remoteUrl || a.url));
      return !this.isUploadingAttachments && text.length > 0 && hasReadyAttachments;
    },
    maxAttachments() {
      return 1;
    },
  },
  watch: {
    catalogId(id) {
      if (id) {
        this.pagination.page = 1;
        this.getPromptTemplates();
      }
    },
  },
  methods: {
    commitSearch() {
      const q = (this.searchQuery || '').trim();
      if (this.lastListFetchKey && q === this.lastListFetchKey.search && this.lastListFetchKey.page === 1) {
        return;
      }
      this.pagination.page = 1;
      this.getPromptTemplates();
    },
    onDialogInput(open) {
      if (!open) this.onClose();
    },
    templateThumbUrls(t) {
      const urls = Array.isArray(t.images) ? t.images : [];
      return Array.isArray(urls) ? urls : [];
    },
    templateAttachmentCount(t) {
      return this.templateThumbUrls(t).length;
    },
    templateAttachmentOverflowCount(t) {
      const n = this.templateAttachmentCount(t);
      return Math.max(0, n - this.templateTilePreviewLimit);
    },
    checkIsVideoUrl(url) {
      return isVideoUrl(url);
    },
    toggleSelectPrompt(template) {
      if (this.selectedTemplateId === template._id) {
        this.message = '';
        this.attachments = [];
        this.selectedTemplateId = null;
        return;
      }
    },
    selectTemplate(template) {
      this.selectedTemplateId = template._id;
      this.message = (template.content || '').trim();

      this.attachments.forEach(a => {
        if (a?.localUrl) URL.revokeObjectURL(a.localUrl);
      });
      this.attachments = [];
      this.showAllAttachments = false;

      const attachmentUrls = Array.isArray(template.images) ? template.images : [];
      if (Array.isArray(attachmentUrls)) {
        this.attachments = attachmentUrls.map(url => {
          const u = String(url || '').trim();
          const isVideo = this.checkIsVideoUrl(u);
          return {
            id: this.objectId(),
            file: null,
            name: filenameFromMediaUrl(imageHelpers.url(u), isVideo),
            url: imageHelpers.url(u),
            localUrl: '',
            remoteUrl: imageHelpers.url(u),
            status: 'ready',
            isVideo,
          };
        });
      }
    },

    openAddMediaFromUrlDialog() {
      if (this.attachments.length >= this.maxAttachments) {
        this.notifyError(`You can only attach up to ${this.maxAttachments} files.`);
        return;
      }
      this.showAddMediaUrlModal = true;
    },

    onSaveMediaFromUrlDialog(payload) {
      const items =
        Array.isArray(payload?.items) && payload.items.length > 0
          ? payload.items.map(it => ({
              url: it?.url != null ? String(it.url).trim() : '',
              isVideo: !!it?.isVideo,
            }))
          : Array.isArray(payload?.urls)
          ? payload.urls.map(u => ({ url: String(u || '').trim(), isVideo: false }))
          : [];
      const ready = items.filter(it => it.url);
      if (!ready.length) {
        this.notifyError('Add at least one URL that loads successfully.');
        return;
      }
      const slots = Math.max(0, this.maxAttachments - this.attachments.length);
      if (slots <= 0) {
        this.notifyError(`You can only attach up to ${this.maxAttachments} files.`);
        return;
      }
      const toAdd = ready.slice(0, slots);
      toAdd.forEach(({ url, isVideo }) => {
        this.attachments.push({
          id: this.objectId(),
          file: null,
          name: filenameFromMediaUrl(url, isVideo),
          url,
          localUrl: '',
          remoteUrl: url,
          status: 'ready',
          isVideo,
        });
      });
      if (ready.length > slots) {
        this.notifyError(`Only ${slots} more attachment(s) could be added.`);
      }
      this.showAllAttachments = false;
    },
    openPreview(att) {
      if (!att?.url || att.status !== 'ready') return;
      this.preview.src = att.url;
      this.preview.isVideo = !!att.isVideo;
      this.preview.show = true;
    },
    closePreview() {
      this.preview.show = false;
      this.preview.src = '';
      this.preview.isVideo = false;
    },
    removeAttachment(id) {
      const att = this.attachments.find(a => a.id === id);
      if (att && this.preview.src && (att.url === this.preview.src || att.remoteUrl === this.preview.src)) {
        this.closePreview();
      }
      attachmentUploadMixin.methods.removeAttachment.call(this, id);
    },
    onPaginationPage(page) {
      const next = Number(page);
      if (!next || next === this.pagination.page) return;
      this.pagination.page = next;
      this.getPromptTemplates();
    },
    async getPromptTemplates() {
      if (!this.catalogId) return;
      const search = (this.searchQuery || '').trim();
      this.templatesLoading = true;
      try {
        const { data } = await reportApi.getLibraryContentShared({
          catalogIds: this.catalogId,
          page: String(this.pagination.page),
          limit: String(this.pagination.limit),
          ...(search && { search }),
        });

        this.promptTemplates = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const p = data?.pagination || {};
        this.pagination.total = p.total || 0;
        this.pagination.totalPage = p.totalPages || 1;
      } catch (error) {
        this.promptTemplates = [];
        this.pagination.total = 0;
        this.pagination.totalPage = 1;
      } finally {
        this.templatesLoading = false;
      }
    },
    onSubmit() {
      if (!this.canApply) return;
      if (this.attachments && this.attachments.length > 1) {
        this.notifyError('You can only attach up to 1 image per message.');
        return;
      }
      const isInvalidImageUrl = this.attachments.some(
        attachment => !isAllowedImageUrl(attachment.remoteUrl || attachment.url),
      );
      if (isInvalidImageUrl) {
        this.notifyError('Only PNG, JPG, JPEG, and WebP image URLs are supported.');
        return;
      }
      this.$emit('submit', {
        message: this.message.trim(),
        attachments: this.attachments.map(attachment => ({
          id: attachment.id,
          name: attachment.name,
          url: attachment.remoteUrl || attachment.url,
          src: attachment.remoteUrl || attachment.url,
          remoteUrl: attachment.remoteUrl || attachment.url,
          status: attachment.status,
          isVideo: !!attachment.isVideo,
        })),
      });
      this.closePreview();
    },
    onClose() {
      this.closePreview();
      this.showAddMediaUrlModal = false;
      this.$emit('close');
    },
  },
};
</script>
<style lang="scss" scoped>
.library-shell {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  --main-text-color: #1f1f1f;
  --main-background: #e9eef6;
}

.library-panel {
  width: 100%;
  max-width: 1400px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.library-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  border-radius: 16px !important;
}

.library-card__title-text {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--main-text-color);
}

.library-card__title {
  flex: 0 0 auto;
  border-bottom: 1px solid var(--v-border-base);
  padding: 16px 20px !important;
}

.library-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.library-card__actions {
  flex: 0 0 auto;
  border-top: 1px solid var(--v-border-base);
}

.library-layout {
  display: flex;
  gap: 0;
  min-height: min(65vh, 640px);
  max-height: min(72vh, 760px);
}

.library-left {
  width: 60%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--v-border-base);
}

.library-left__search {
  flex: 0 0 auto;
  margin-bottom: 16px;
  padding: 16px 16px 0 16px;
}

.library-left__pagination {
  flex: 0 0 auto;
  padding-block: 8px;
  border-top: 1px solid var(--v-border-base);
}

.library-left__loading {
  flex: 1 1 auto;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.library-left__scroller {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -4px;
  padding: 4px 4px 8px;
}

.library-left__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;
  flex: 0 1 auto;
  min-height: min-content;
  padding: 0 16px 16px 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.library-left__empty {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

/* Template tile — content on top, compact media strip at bottom */
.template-tile {
  --tile-primary: var(--v-primary-base, #094547);
  --tile-primary-rgb: 9, 69, 71;
  position: relative;

  width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  padding: 0;
  border-radius: 14px;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid var(--v-border-base);
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    background: rgba(var(--tile-primary-rgb), 0.1);
    border-color: var(--tile-primary);
  }

  &--active {
    background: rgba(var(--tile-primary-rgb), 0.1);
    border-color: var(--tile-primary);
  }
}

.template-tile__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 0;
  color: var(--v-primary-base);
  i {
    color: var(--v-primary-base);
  }
  font-size: 12px;
  font-weight: 500;
}

.template-tile__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 10px;
}

.template-tile__footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 6px 10px 8px;
  background: rgba(15, 23, 42, 0.04);
  border-top: 1px solid var(--v-border-base);
  flex: 0 0 auto;
}

.template-tile:hover .template-tile__footer {
  background: rgba(15, 23, 42, 0.055);
}

.template-tile--active .template-tile__footer {
  background: rgba(var(--tile-primary-rgb), 0.08);
  border-top-color: rgba(var(--tile-primary-rgb), 0.12);
}

.template-tile__toggle {
  position: absolute;
  top: 8px;
  right: 8px;
  border: 1px solid var(--v-primary-base);
  border-radius: 4px;
  height: 16px !important;
  width: 16px !important;
}

.template-tile__media {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  width: 100%;
  min-width: 0;
}

.template-tile__media--empty {
  justify-content: center;
}

.template-tile__overflow {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 8px;
  border: 1px dashed rgba(var(--tile-primary-rgb), 0.45);
  background: rgba(var(--tile-primary-rgb), 0.08);
  color: var(--tile-primary);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.template-tile--active .template-tile__overflow {
  background: rgba(var(--tile-primary-rgb), 0.14);
  border-color: rgba(var(--tile-primary-rgb), 0.55);
}

.template-tile__placeholder {
  opacity: 0.5;
}

.template-tile__placeholder-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.template-tile__thumb {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;

  &--video {
    display: grid;
    place-items: center;
    position: relative;
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 6px;
      background: rgba(0, 0, 0, 0.38);
      z-index: 1;
    }
    &-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
    }
  }
}

.template-tile__prompt {
  margin: 0;
  padding: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--main-text-color);
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: break-word;
  flex: 1 1 auto;
  min-height: 0;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.template-tile__hint {
  font-size: 11px;
}

.library-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: #fff;
}

.prompt-templates-library__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.library-right__upload-toolbar {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.library-right__upload-zone {
  flex: 1 1 auto;
  min-width: 0;
  border: 1.5px dashed rgba(0, 0, 0, 0.16);
  border-radius: 14px;
  padding: 18px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  outline: none;
  background: #fafafa;

  &:hover,
  &:focus {
    border-color: var(--v-primary-base, #094547);
    background: rgba(9, 69, 71, 0.04);
  }

  .upload-zone__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--main-text-color);
  }

  .upload-zone__subtitle {
    font-size: 12px;
    color: var(--main-text-color);
    opacity: 0.5;
  }
}

.library-right__url-btn {
  flex: 0 0 auto;
  align-self: center;
  text-transform: none;
  letter-spacing: 0;
  padding: 12px 16px !important;
  width: 100%;
  border: 1px solid var(--v-border-base);
  border-radius: 10px;
  background: #fafafa;
}

.library-right__attachments {
  flex: 0 0 auto;
}

.attachments-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-block: 4px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
}

.attachment {
  position: relative;
  flex: 0 0 auto;
}

.attachment__thumb {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f0f0f0;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;

  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
}

.attachment__thumb--video {
  position: relative;
  display: grid;
  place-items: center;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }
  &-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
}

.attachment__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.attachment__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
}

.attachment__overlay--error {
  background: rgba(211, 47, 47, 0.65);
}

.attachment__remove {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  z-index: 2;
}

.attachments-more {
  flex: 0 0 auto;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px dashed rgba(0, 0, 0, 0.18);
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.3s ease;
  color: var(--main-text-color);

  &:hover {
    background: var(--main-background);
  }
}

.library-right__prompt-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--main-text-color);
  margin-bottom: 8px;
}

.library-right__textarea {
  flex: 1 1 auto;
  min-height: 160px;
  width: 100%;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  resize: vertical;
  outline: none;
  font-family: inherit;
  color: var(--main-text-color);

  &:focus {
    border-color: var(--v-primary-base, #094547);
  }
}

.image-generation-dialog__current-model {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(25, 118, 210, 0.06);
  border: 1px solid rgba(25, 118, 210, 0.14);
  color: var(--main-text-color);
}

@media (max-width: 960px) {
  .library-layout {
    flex-direction: column;
    max-height: none;
    min-height: auto;
  }

  .library-left {
    width: 100%;
    max-width: none;
    min-width: 0;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    max-height: 42vh;
  }

  .library-left__scroller {
    overflow-x: hidden;
    overflow-y: auto;
  }

  .library-right {
    padding-top: 12px;
  }
}
</style>
